import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "../Food/RecipeList.css";
import "./BabyTracker.css";
import {
  defaultBabyEventForm,
  deleteBabyEvent,
  eventCategoryLabels,
  eventCategoryPluralLabels,
  eventTypeOptionsForCategory,
  bottleMilkTypes,
  bottleMilkTypeForEvent,
  calculateMinutesBetween,
  feedingSides,
  feedingUnitForType,
  formatDurationFromMinutes,
  formatElapsedSeconds,
  formatFeedingAmount,
  fetchBabyEvents,
  getNowLocalDateTime,
  normalizeFeedingType,
  saveBabyEvent,
  saveBabyEventNotes,
  sleepEventDisplay,
} from "./BabyTrackerShared";

const categoryIntro = {
  diaper: "Track wet, dirty, mixed, and dry diaper checks.",
  feeding: "Track breast feeding, bottle feeds, amounts, and notes.",
  pumping: "Track pumping sessions, timing, and ounces collected.",
};

const defaultTypeForCategory = (category) => eventTypeOptionsForCategory(category)[0];

const BabyTrackerCategoryPage = ({ category }) => {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(defaultBabyEventForm(category, defaultTypeForCategory(category)));
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerPaused, setTimerPaused] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const notesSaveTimer = useRef(null);
  const timerStartedAtMs = useRef(null);
  const timerAccumulatedSeconds = useRef(0);
  const timerDurationOverride = useRef(false);
  const isFeeding = category === "feeding";
  const isPumping = category === "pumping";
  const isBreastFeeding = isFeeding && form.eventType === "Breast";
  const isBottleFeeding = isFeeding && form.eventType === "Bottle";
  const title = eventCategoryPluralLabels[category];

  const refreshEvents = useCallback(() => {
    fetchBabyEvents(category).then((data) => setEvents(data || []));
  }, [category]);

  useEffect(() => {
    setForm(defaultBabyEventForm(category, defaultTypeForCategory(category)));
    setEditingId(null);
    setShowForm(false);
    setStatusMessage("");
    setTimerRunning(false);
    setTimerPaused(false);
    setElapsedSeconds(0);
    timerStartedAtMs.current = null;
    timerAccumulatedSeconds.current = 0;
    timerDurationOverride.current = false;
  }, [category]);

  useEffect(() => {
    refreshEvents();
  }, [refreshEvents]);

  useEffect(() => {
    if (!timerRunning || !timerStartedAtMs.current) return undefined;
    const intervalId = setInterval(() => {
      const currentRunSeconds = Math.max(0, Math.floor((Date.now() - timerStartedAtMs.current) / 1000));
      setElapsedSeconds(timerAccumulatedSeconds.current + currentRunSeconds);
    }, 1000);
    return () => clearInterval(intervalId);
  }, [timerRunning]);

  useEffect(() => {
    if (!editingId || !showForm) return undefined;
    if (notesSaveTimer.current) {
      clearTimeout(notesSaveTimer.current);
    }
    notesSaveTimer.current = setTimeout(() => {
      saveBabyEventNotes(editingId, form.notes || "").catch(() => {
        setStatusMessage("Notes could not autosave.");
      });
    }, 900);
    return () => {
      if (notesSaveTimer.current) {
        clearTimeout(notesSaveTimer.current);
      }
    };
  }, [editingId, form.notes, showForm]);

  useEffect(() => {
    if (!isBreastFeeding || !form.occurredAt || !form.endedAt || timerDurationOverride.current) return;
    const minutes = calculateMinutesBetween(form.occurredAt, form.endedAt);
    if (minutes !== "" && minutes !== String(form.feedingAmount)) {
      setForm((currentValue) => ({ ...currentValue, feedingAmount: minutes }));
    }
  }, [form.occurredAt, form.endedAt, form.feedingAmount, isBreastFeeding]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((currentValue) => {
      if (name === "eventType" && category === "feeding") {
        timerDurationOverride.current = false;
        return {
          ...currentValue,
          eventType: value,
          feedingUnit: feedingUnitForType(value),
          feedingMilkType: value === "Bottle" ? currentValue.feedingMilkType || "Formula" : "",
          feedingSide: value === "Breast" ? currentValue.feedingSide : "",
          endedAt: value === "Breast" ? currentValue.endedAt : "",
        };
      }
      if (category === "pumping" && name === "eventType") {
        return {
          ...currentValue,
          eventType: value,
          feedingUnit: "oz",
          feedingMilkType: "",
          feedingSide: "",
        };
      }
      if (name === "occurredAt" || name === "endedAt") {
        timerDurationOverride.current = false;
      }
      return { ...currentValue, [name]: value };
    });
  };

  const resetBreastFeedingTimer = () => {
    setTimerRunning(false);
    setTimerPaused(false);
    setElapsedSeconds(0);
    timerStartedAtMs.current = null;
    timerAccumulatedSeconds.current = 0;
    timerDurationOverride.current = false;
  };

  const getCurrentTimerSeconds = () => {
    if (!timerRunning || !timerStartedAtMs.current) return elapsedSeconds;
    const currentRunSeconds = Math.max(0, Math.floor((Date.now() - timerStartedAtMs.current) / 1000));
    return timerAccumulatedSeconds.current + currentRunSeconds;
  };

  const formatTimerMinutes = (seconds) => String(Math.max(0, Math.round(seconds)) / 60);

  const startBreastFeedingTimer = () => {
    if (timerPaused) {
      timerStartedAtMs.current = Date.now();
      timerAccumulatedSeconds.current = elapsedSeconds;
      setTimerPaused(false);
      setTimerRunning(true);
      setStatusMessage("Breast feeding timer resumed.");
      return;
    }

    const startTime = getNowLocalDateTime();
    const timerForm = {
      ...form,
      eventCategory: category,
      eventType: "Breast",
      occurredAt: startTime,
      endedAt: "",
      feedingAmount: "",
      feedingUnit: "min",
      feedingMilkType: "",
      feedingSide: form.feedingSide || "",
    };
    timerDurationOverride.current = false;
    timerStartedAtMs.current = Date.now();
    timerAccumulatedSeconds.current = 0;
    setTimerRunning(true);
    setTimerPaused(false);
    setElapsedSeconds(0);
    setForm(timerForm);
    if (!editingId) {
      saveBabyEvent(timerForm)
        .then((data) => {
          const savedId = data?.event?.id || data?.id;
          if (savedId) {
            setEditingId(savedId);
            setStatusMessage("Breast feeding timer started. Notes will autosave.");
            refreshEvents();
          }
        })
        .catch(() => {
          setTimerRunning(false);
          setStatusMessage("Unable to start timer.");
        });
    }
  };

  const pauseBreastFeedingTimer = () => {
    const currentElapsedSeconds = getCurrentTimerSeconds();
    timerAccumulatedSeconds.current = currentElapsedSeconds;
    timerStartedAtMs.current = null;
    setElapsedSeconds(currentElapsedSeconds);
    setTimerRunning(false);
    setTimerPaused(true);
    setStatusMessage("Breast feeding timer paused.");
  };

  const stopBreastFeedingTimer = () => {
    const endTime = getNowLocalDateTime();
    const finalElapsedSeconds = getCurrentTimerSeconds();
    timerDurationOverride.current = true;
    timerAccumulatedSeconds.current = finalElapsedSeconds;
    timerStartedAtMs.current = null;
    const stoppedForm = {
      ...form,
      endedAt: endTime,
      feedingAmount: formatTimerMinutes(finalElapsedSeconds),
    };
    setTimerRunning(false);
    setTimerPaused(false);
    setElapsedSeconds(finalElapsedSeconds);
    setForm(stoppedForm);
    if (editingId) {
      saveBabyEvent(stoppedForm, editingId)
        .then(() => {
          setStatusMessage("Breast feeding timer stopped.");
          refreshEvents();
        })
        .catch(() => {
          setStatusMessage("Unable to save stopped timer.");
        });
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setStatusMessage("");
    const submittedEndTime = isBreastFeeding && (timerRunning || timerPaused) ? getNowLocalDateTime() : form.endedAt;
    const submittedTimerSeconds = getCurrentTimerSeconds();
    const eventForm = {
      ...form,
      endedAt: submittedEndTime,
      feedingUnit: category === "feeding" ? feedingUnitForType(form.eventType) : category === "pumping" ? "oz" : "",
      feedingAmount: isBreastFeeding && (timerRunning || timerPaused) ? formatTimerMinutes(submittedTimerSeconds) : form.feedingAmount,
      feedingMilkType: category === "feeding" && form.eventType === "Bottle" ? form.feedingMilkType || "Formula" : "",
      feedingSide: category === "feeding" && form.eventType === "Breast" ? form.feedingSide : "",
    };
    saveBabyEvent(eventForm, editingId)
      .then(() => {
        setStatusMessage(editingId ? `${eventCategoryLabels[category]} event updated.` : `${eventCategoryLabels[category]} event logged.`);
        setEditingId(null);
        setShowForm(false);
        resetBreastFeedingTimer();
        setForm(defaultBabyEventForm(category, defaultTypeForCategory(category)));
        refreshEvents();
      })
      .catch(() => {
        setStatusMessage("Unable to save event.");
      });
  };

  const beginEdit = (entry) => {
    setEditingId(entry.id);
    setShowForm(true);
    setStatusMessage("");
    const normalizedEventType = normalizeFeedingType(entry.eventType);
    setForm({
      eventCategory: category,
      eventType: normalizedEventType,
      occurredAt: entry.occurredAtInput,
      endedAt: entry.endedAtInput || "",
      feedingAmount: entry.feedingAmount ?? "",
      feedingUnit: entry.feedingUnit || feedingUnitForType(normalizedEventType),
      feedingMilkType: normalizedEventType === "Bottle" ? bottleMilkTypeForEvent(entry) : "",
      feedingSide: normalizedEventType === "Breast" ? entry.feedingSide || "" : "",
      notes: entry.notes || "",
    });
    setTimerRunning(false);
    setTimerPaused(false);
    setElapsedSeconds(0);
    timerStartedAtMs.current = null;
    timerAccumulatedSeconds.current = 0;
    timerDurationOverride.current = false;
  };

  const removeEvent = (eventId) => {
    deleteBabyEvent(eventId).then(() => {
      setStatusMessage(`${eventCategoryLabels[category]} event deleted.`);
      setEditingId(null);
      setShowForm(false);
      resetBreastFeedingTimer();
      setForm(defaultBabyEventForm(category, defaultTypeForCategory(category)));
      refreshEvents();
    });
  };

  return (
    <div className="BabyTrackerPage">
      <div className="searchBarDiv formViewWrapper BabyTrackerIntro">
        <div>
          <h2 className="BabyTrackerTitle">{title}</h2>
          <p className="BabyTrackerSubtitle">{categoryIntro[category]}</p>
        </div>
        <Link className="summaryButton BabyTrackerLinkButton" to="/baby-tracker">
          Home
        </Link>
      </div>

      <div className="BabyTrackerGrid">
        <div className="searchBarDiv formViewWrapper BabyTrackerPanel BabyTrackerSinglePanel">
          {!showForm ? (
            <button
              className="summaryButton BabyTrackerAddToggleButton"
              onClick={() => {
                setEditingId(null);
                resetBreastFeedingTimer();
                setForm(defaultBabyEventForm(category, defaultTypeForCategory(category)));
                setShowForm(true);
              }}
            >
              Add {eventCategoryLabels[category]}
            </button>
          ) : (
            <div className="BabyTrackerFormBlock">
              <h2 className="BabyTrackerTitle">{editingId ? `Edit ${eventCategoryLabels[category].toLowerCase()} event` : `Add ${eventCategoryLabels[category].toLowerCase()} event`}</h2>
              <form onSubmit={handleSubmit}>
                <div className="BabyTrackerTwoColumn">
                  <div className="inputDiv">
                    <label>Type</label>
                    <select
                      className="inputBox addInput"
                      name="eventType"
                      value={form.eventType}
                      onChange={handleChange}
                    >
                      {eventTypeOptionsForCategory(category).map((typeValue) => (
                        <option key={typeValue} value={typeValue}>
                          {typeValue}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="inputDiv">
                    <label>Occurred</label>
                    <input
                      type="datetime-local"
                      step="1"
                      className="inputBox addInput"
                      name="occurredAt"
                      value={form.occurredAt}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {isFeeding ? (
                  <>
                    {isBreastFeeding ? (
                      <div className="BabyTrackerTimerPanel">
                        <div>
                          <span>Elapsed</span>
                          <strong>{formatElapsedSeconds(timerRunning || timerPaused ? elapsedSeconds : Number(form.feedingAmount || 0) * 60)}</strong>
                        </div>
                        <div className="BabyTrackerActionRow">
                          <button
                            className="summaryButton"
                            disabled={timerRunning}
                            onClick={startBreastFeedingTimer}
                            type="button"
                          >
                            {timerPaused ? "Resume" : "Start"}
                          </button>
                          <button
                            className="summaryButton"
                            disabled={!timerRunning}
                            onClick={pauseBreastFeedingTimer}
                            type="button"
                          >
                            Pause
                          </button>
                          <button
                            className="summaryButton"
                            disabled={!timerRunning && !timerPaused}
                            onClick={stopBreastFeedingTimer}
                            type="button"
                          >
                            Stop
                          </button>
                        </div>
                      </div>
                    ) : null}
                    <div className="BabyTrackerTwoColumn">
                      <div className="inputDiv">
                        <label>{isBreastFeeding ? "Minutes" : "Amount (oz)"}</label>
                        <input
                          className="inputBox addInput"
                          name="feedingAmount"
                          value={isBreastFeeding ? formatDurationFromMinutes(form.feedingAmount) : form.feedingAmount}
                          onChange={handleChange}
                          inputMode="decimal"
                          readOnly={isBreastFeeding}
                        />
                      </div>
                      {isBreastFeeding ? (
                        <div className="inputDiv">
                          <label>Ended</label>
                          <input
                            type="datetime-local"
                            step="1"
                            className="inputBox addInput"
                            name="endedAt"
                            value={form.endedAt}
                            onChange={handleChange}
                          />
                        </div>
                      ) : null}
                    {isBreastFeeding ? (
                      <div className="inputDiv">
                        <label>Started side</label>
                        <select
                          className="inputBox addInput"
                          name="feedingSide"
                          value={form.feedingSide}
                          onChange={handleChange}
                        >
                          {feedingSides.map((sideValue) => (
                            <option key={sideValue || "none"} value={sideValue}>
                              {sideValue || "N/A"}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : null}
                    {isBottleFeeding ? (
                      <div className="inputDiv">
                        <label>Bottle type</label>
                        <select
                          className="inputBox addInput"
                          name="feedingMilkType"
                          value={form.feedingMilkType || "Formula"}
                          onChange={handleChange}
                        >
                          {bottleMilkTypes.map((milkType) => (
                            <option key={milkType} value={milkType}>
                              {milkType}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : null}
                    </div>
                  </>
                ) : null}

                {isPumping ? (
                  <div className="BabyTrackerTwoColumn">
                    <div className="inputDiv">
                      <label>Ended</label>
                      <input
                        type="datetime-local"
                        step="1"
                        className="inputBox addInput"
                        name="endedAt"
                        value={form.endedAt}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="inputDiv">
                      <label>Ounces collected</label>
                      <input
                        className="inputBox addInput"
                        name="feedingAmount"
                        value={form.feedingAmount}
                        onChange={handleChange}
                        inputMode="decimal"
                      />
                    </div>
                  </div>
                ) : null}

                <div className="inputDiv">
                  <label>Notes</label>
                  <textarea
                    className="inputBox textAreaAdd BabyTrackerTextarea"
                    name="notes"
                    value={form.notes}
                    onChange={handleChange}
                  />
                </div>

                <div className="BabyTrackerActionRow">
                  <button type="submit" className="summaryButton">
                    Submit
                  </button>
                  <button
                    type="button"
                    className="summaryButton"
                    onClick={() => {
                      setEditingId(null);
                      setShowForm(false);
                      resetBreastFeedingTimer();
                      setForm(defaultBabyEventForm(category, defaultTypeForCategory(category)));
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}
          {statusMessage ? <p className="BabyTrackerStatus">{statusMessage}</p> : null}
        </div>

        <div className="shoppingListDiv BabyTrackerPanel">
          <h2 className="BabyTrackerTitle">Recent {title.toLowerCase()}</h2>
          {events.length ? (
            events.map((entry) => {
              const sleepDisplay = category === "sleep" ? sleepEventDisplay(entry.eventType) : null;
              return (
                <div className={sleepDisplay ? "BabyTrackerHistoryCard BabyTrackerSleepCard" : "BabyTrackerHistoryCard"} key={entry.id}>
                  {sleepDisplay ? (
                    <div className="BabyTrackerSleepHeader">
                      <span className={`BabyTrackerSleepBadge BabyTrackerSleepBadge-${sleepDisplay.tone}`}>
                        {entry.eventType}
                      </span>
                      <div>
                        <strong>{sleepDisplay.label}</strong>
                        <div>{entry.occurredAt}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="BabyTrackerHistoryRow">
                      <div>
                        <strong>{category === "feeding" ? normalizeFeedingType(entry.eventType) : entry.eventType}</strong>
                        <div>{entry.occurredAt}</div>
                      </div>
                      {entry.endedAt ? <span>Ended {entry.endedAt}</span> : null}
                    </div>
                  )}
                  {entry.feedingAmount ? (
                    <div className="BabyTrackerHistoryMeta">
                      <span>{formatFeedingAmount(entry)}</span>
                      {normalizeFeedingType(entry.eventType) === "Bottle" ? <span>{bottleMilkTypeForEvent(entry)}</span> : null}
                      {entry.feedingSide ? <span>{entry.feedingSide}</span> : null}
                    </div>
                  ) : null}
                  {entry.notes ? <p className="BabyTrackerNotes">{entry.notes}</p> : null}
                  <div className="BabyTrackerActionRow">
                    <button className="summaryButton" onClick={() => beginEdit(entry)}>
                      Edit
                    </button>
                    <button className="summaryButton" onClick={() => removeEvent(entry.id)}>
                      Delete
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            <p>No {title.toLowerCase()} logged yet.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BabyTrackerCategoryPage;
